import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getUserDataWithUsername, IUserProps } from '../common/data/userDummyData';
import { useAppSelector } from '../custom/redux/reduxHooks';
import api, { backendUrl } from '../custom/constants/urls';

export interface IAuthContextProps {
	user: string;
	setUser?(...args: unknown[]): unknown;
	userData: Partial<IUserProps>;
	userPermissions: string[];
	isLoading: boolean;
	getUserPermissions: () => Promise<void>;
}
const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}
export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	const [user, setUser] = useState<string>(localStorage.getItem('facit_authUsername') || '');
	const [userData, setUserData] = useState<Partial<IUserProps>>({});

	useEffect(() => {
		localStorage.setItem('facit_authUsername', user);
	}, [user]);

	useEffect(() => {
		if (user !== '') {
			setUserData(getUserDataWithUsername(user));
		} else {
			setUserData({});
		}
	}, [user]);

	const { user: adminUser } = useAppSelector((state) => state.auth);
	const [userPermissions, setUserPermissions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const getUserPermissions = async () => {
		try {
			if (!adminUser?.id) return;

			const res = await api.get(
				`${backendUrl}/admin/permissions/user?userId=${adminUser.id}`,
			);
			const permissionNames = res.data.data.permissions.map(
				(p: { permName: string }) => p.permName,
			);
			setUserPermissions(permissionNames);
		} catch (error) {
			console.error('Failed to fetch user permissions:', error);
			setUserPermissions([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getUserPermissions();
	}, [adminUser?.id]);

	const value = useMemo(
		() => ({
			user,
			setUser,
			userData,
			userPermissions,
			isLoading,
			getUserPermissions,
		}),
		[user, userData, userPermissions, isLoading],
	);
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
AuthContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AuthContext;
